import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useAppDispatch, useAppSelector } from "hooks";
import { setModalxComponent } from "components/modalx/modalx.slice";
import { Badge } from "containers/settings/badge";
import { AddButton, FIELD_INPUT_CLASS } from "containers/settings/catalog-form-parts";
import { CatalogTable, SettingsCard } from "containers/settings/ui";
import { fetchUsers, setFilterValue } from "./users.slice";

/** One person of the company as `fetch-all-users` sends them. */
interface UserRow {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  position: string | null;
  isDisabled: boolean;
}

/** What the slice keeps under `filtersValue`; both are absent until the person uses the control. */
interface UsersFilters {
  isDisabled?: string[];
  textSearch?: string;
}

// The status buttons, left to right, and the filter each one sends. The backend narrows
// the list by status only when it gets exactly one value, so «Visi» sends none.
const STATUS_FILTERS = [
  { labelKey: "users_filter_active", isDisabled: ["not_disabled"] },
  { labelKey: "users_filter_disabled", isDisabled: ["is_disabled"] },
  { labelKey: "all", isDisabled: [] as string[] },
];

// On a phone the name link stretches over its whole card (the card is its positioning box, see CatalogTable),
// so a tap anywhere on the card opens the person, and the link stays the one control to focus with a keyboard.
const NAME_LINK_CLASS =
  "font-medium text-indigo-600 hover:underline max-md:after:absolute max-md:after:inset-0 max-md:focus-visible:after:ring-2 max-md:focus-visible:after:ring-inset max-md:focus-visible:after:ring-indigo-500";

// The backend searches only for text longer than 3 characters. The search follows typing after this pause.
const SEARCH_DELAY_MS = 300;

export default function Users() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const { isUserMaster } = useAppSelector((state) => state.companySelectReducer);
  const { usersCollection, isLoading, isError, filtersValue } = useAppSelector(
    (state) => state.usersReducer
  );
  const filters: UsersFilters = filtersValue;
  const appliedSearch = filters.textSearch ?? "";
  const [searchText, setSearchText] = useState(appliedSearch);

  useEffect(() => {
    if (companyId) dispatch(fetchUsers(companyId, filters));
  }, [dispatch, companyId, filters]);

  useEffect(() => {
    if (searchText === appliedSearch) return undefined;
    const timer = setTimeout(
      () => dispatch(setFilterValue({ key: "textSearch", value: searchText })),
      SEARCH_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [dispatch, searchText, appliedSearch]);

  // The slice is plain JavaScript, so its list has no type of its own.
  const users = usersCollection as UserRow[];
  const appliedStatus = (filters.isDisabled ?? []).join();

  return (
    <SettingsCard>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-72">
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t("users_search_placeholder")}
            aria-label={t("users_search_placeholder")}
            className={FIELD_INPUT_CLASS}
          />
        </div>
        <div role="group" aria-label={t("user_status")} className="flex gap-2">
          {STATUS_FILTERS.map((filter) => {
            const isChosen = filter.isDisabled.join() === appliedStatus;
            return (
              <button
                key={filter.labelKey}
                type="button"
                aria-pressed={isChosen}
                onClick={() => dispatch(setFilterValue({ key: "isDisabled", value: filter.isDisabled }))}
                className={clsx(
                  "rounded-full border px-3 py-1 text-sm font-medium max-md:min-h-11",
                  isChosen
                    ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                )}
              >
                {t(filter.labelKey)}
              </button>
            );
          })}
        </div>
        {isUserMaster && (
          <div className="ml-auto">
            <AddButton label={t("users_add")} onClick={() => dispatch(setModalxComponent("UserForm"))} />
          </div>
        )}
      </div>

      <CatalogTable
        // Below xl the table has no room for position and phone columns: from md they go under the name,
        // and a phone card lists every column as its own line.
        columns={[
          { key: "name", header: t("name") },
          { key: "position", header: t("user_position"), className: "hidden xl:table-cell" },
          { key: "email", header: t("email"), className: "max-xl:break-all" },
          { key: "phone", header: t("phone"), className: "hidden whitespace-nowrap xl:table-cell" },
          { key: "status", header: t("user_status") },
        ]}
        rows={users.map((user) => {
          const positionAndPhone = [user.position, user.phone].filter(Boolean).join(" · ");
          return {
            id: user.id,
            cells: {
              name: (
                <>
                  {isUserMaster ? (
                    <Link
                      to={`/app/${companyId}/users/${user.id}`}
                      className={NAME_LINK_CLASS}
                    >
                      {user.name || user.email}
                    </Link>
                  ) : (
                    <span className="font-medium">{user.name || user.email}</span>
                  )}
                  {positionAndPhone && (
                    <div className="hidden text-xs text-gray-500 md:block xl:hidden">{positionAndPhone}</div>
                  )}
                </>
              ),
              position: user.position || "—",
              email: user.email,
              phone: user.phone,
              status: (
                <Badge tone={user.isDisabled ? "gray" : "green"}>
                  {user.isDisabled ? t("is_disabled") : t("not_disabled")}
                </Badge>
              ),
            },
          };
        })}
        empty={t("users_empty")}
        isLoading={isLoading}
        loadError={isError ? t("settings_load_failed") : undefined}
      />
    </SettingsCard>
  );
}
